import { motion, AnimatePresence } from 'framer-motion';
import { X, Cloud, Award, TrendingUp, Users, CheckCircle } from 'lucide-react';

interface CloudPriveBZModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CloudPriveBZModal({ isOpen, onClose }: CloudPriveBZModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-2xl max-w-6xl w-full my-8 border border-blue-500/30 shadow-2xl shadow-blue-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-blue-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
                    <Cloud className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                      Cloud Privé BZ
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Excellence en infrastructure cloud - Rapport qualité/prix incomparable
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-blue-600/20 via-cyan-600/20 to-blue-600/20 rounded-xl p-8 border-2 border-blue-500/50">
                <h3 className="text-3xl font-bold text-center text-white mb-4">
                  Notre Cloud Privé : Notre Fierté
                </h3>
                <p className="text-gray-300 text-lg text-center leading-relaxed">
                  Le cloud privé de BZ Inc. offre un <strong className="text-white">excellent rapport qualité/prix</strong> avec des 
                  <strong className="text-white"> standards de qualité très élevés</strong> et un 
                  <strong className="text-white"> service humain</strong> qui rend cette dimension de l'infonuagique une option 
                  <strong className="text-blue-400"> très appréciée des clients du Québec et du Canada</strong>.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Award className="w-7 h-7 text-blue-400" />
                    Standards de Qualité Élevés
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Bien que nous ne soyons pas certifiés TIER X, SOC2 ou autres certifications formelles, 
                    nos <strong className="text-white">standards de qualité sont très élevés</strong> et répondent 
                    aux besoins de nos clients avec excellence.
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span>Infrastructure robuste et redondante</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span>Deux centres de données géographiquement séparés</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span>Monitoring et maintenance proactive</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Users className="w-7 h-7 text-cyan-400" />
                    Service Humain Exceptionnel
                  </h4>
                  <p className="text-gray-300 mb-4">
                    C'est notre <strong className="text-white">service humain</strong> qui fait toute la différence. 
                    Support réactif, personnalisé, et dédié. C'est ce qui rend notre cloud privé 
                    <strong className="text-cyan-400"> très apprécié des clients du Québec et du Canada</strong>.
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Support réactif et personnalisé</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Accompagnement dédié</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Relation de confiance à long terme</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-7 h-7 text-indigo-400" />
                  Rapport Qualité/Prix Incomparable
                </h4>
                <p className="text-gray-300">
                  Notre cloud privé offre un <strong className="text-white">excellent rapport qualité/prix</strong>. 
                  Nous offrons deux formules adaptées aux besoins : <strong className="text-indigo-400">Collocation</strong> pour 
                  le contrôle total, et <strong className="text-purple-400">Environnement Partagé (IAAS)</strong> pour une solution 
                  économique. Dans tous les cas, vous bénéficiez de nos standards de qualité et de notre service humain exceptionnel.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-6 border border-blue-500/30">
                  <h5 className="text-blue-400 font-bold text-lg mb-3">Centre de Données BZ</h5>
                  <p className="text-gray-300 text-sm">
                    Notre centre de données dans les locaux de BZ Inc. offre une infrastructure complète 
                    avec tous nos standards de qualité.
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-6 border border-cyan-500/30">
                  <h5 className="text-cyan-400 font-bold text-lg mb-3">Centre de Données Oricom (Serko)</h5>
                  <p className="text-gray-300 text-sm">
                    Notre deuxième centre de données chez Oricom, salle Serko, offre redondance géographique 
                    et haute disponibilité.
                  </p>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-blue-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












